import { useMemo } from "react";
import { motion } from "framer-motion";
import { AlertCircle, ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import {
    useCatalog,
    findPackageByName,
    getAddonsForPackage,
    computePricing,
} from "@/lib/CatalogProvider";
import { SectionHead } from "@/components/inquiry/inquiryFields";
import PricingSummary from "@/components/site/PricingSummary";
import ReviewInvoice from "@/components/inquiry/ReviewInvoice";
import { resolveServiceLabel } from "@/components/inquiry/inquiryServices";

/**
 * StepReview — Final review + submit. Journey-aware:
 *   - Package  → shows an invoice-style summary + coupon field (locked snapshot at submit).
 *   - Custom   → shows selected services + budget instead.
 */
export const StepReview = ({ data, onEdit, submitting, submitError, errors, onSubmit, onApplyCoupon, onRemoveCoupon }) => {
    const { packages, addons } = useCatalog();
    const isCustom = data.inquiry_kind === "custom";

    const selectedPkg = useMemo(
        () => (isCustom || data.is_custom_scope ? null : findPackageByName(packages, data.selected_package)),
        [packages, data.selected_package, data.is_custom_scope, isCustom]
    );

    const availableAddons = useMemo(
        () => (selectedPkg ? getAddonsForPackage(addons, selectedPkg) : []),
        [selectedPkg, addons]
    );

    const selectedAddons = useMemo(
        () => availableAddons.filter((a) => (data.addons || []).includes(a.id)),
        [availableAddons, data.addons]
    );

    const pricing = useMemo(
        () => computePricing({
            selectedPackage: selectedPkg,
            selectedAddons,
            isCustomScope: data.is_custom_scope,
        }),
        [selectedPkg, selectedAddons, data.is_custom_scope]
    );

    const finalBudget =
        data.budget_mode === "custom" && data.custom_budget
            ? `Custom: $${data.custom_budget}`
            : data.estimated_budget || "—";

    // Adaptive step indices for Edit CTAs.
    const packageEditIdx = 1;
    const addonsEditIdx = 2;
    const brandEditIdxPackage = 3;
    const visionEditIdxPackage = 4;
    const timelineEditIdxPackage = 5;
    const contactEditIdxPackage = 6;

    const servicesEditIdxCustom = 1;
    const brandEditIdxCustom = 2;
    const visionEditIdxCustom = 3;
    const budgetEditIdxCustom = 4;
    const contactEditIdxCustom = 5;

    const rows = isCustom
        ? [
            { section: "services", label: "Services", value: (data.services_needed || []).map(resolveServiceLabel).join(", ") || "—" },
            { section: "brand", label: "Business Name", value: data.business_name || "—" },
            { section: "brand", label: "Business Type", value: data.business_type || "—" },
            { section: "brand", label: "Website", value: data.website_link || "—" },
            { section: "brand", label: "Facebook", value: data.facebook_link || "—" },
            { section: "brand", label: "Instagram Page", value: data.instagram_link || "—" },
            { section: "vision", label: "Goals", value: data.goals || "—" },
            { section: "vision", label: "What you need", value: data.what_do_you_need || "—" },
            { section: "vision", label: "Details", value: data.project_details || "—" },
            { section: "budget", label: "Budget", value: finalBudget },
            { section: "budget", label: "Timeline", value: data.expected_timeline || "—" },
            { section: "contact", label: "Full Name", value: data.full_name || "—" },
            { section: "contact", label: "Email", value: data.email || "—" },
            { section: "contact", label: "WhatsApp", value: data.whatsapp_number || "—" },
            { section: "contact", label: "Instagram", value: data.instagram_username || "—" },
            { section: "contact", label: "Preferred Channel", value: data.preferred_communication || "—" },
            { section: "contact", label: "Detected Country", value: data.detected_country || "—" },
        ]
        : [
            // Package journey — package IS the investment. NO budget row here.
            { section: "package", label: "Selected Package", value: data.is_custom_scope ? "Custom Project" : (data.selected_package || "—") },
            { section: "package", label: "Category", value: data.package_category || (data.is_custom_scope ? "Custom" : "—") },
            { section: "brand", label: "Business Name", value: data.business_name || "—" },
            { section: "brand", label: "Business Type", value: data.business_type || "—" },
            { section: "brand", label: "Website", value: data.website_link || "—" },
            { section: "brand", label: "Facebook", value: data.facebook_link || "—" },
            { section: "brand", label: "Instagram Page", value: data.instagram_link || "—" },
            { section: "vision", label: "Goals", value: data.goals || "—" },
            { section: "vision", label: "What you need", value: data.what_do_you_need || "—" },
            { section: "vision", label: "Details", value: data.project_details || "—" },
            { section: "timeline", label: "Timeline", value: data.expected_timeline || "—" },
            { section: "contact", label: "Full Name", value: data.full_name || "—" },
            { section: "contact", label: "Email", value: data.email || "—" },
            { section: "contact", label: "WhatsApp", value: data.whatsapp_number || "—" },
            { section: "contact", label: "Instagram", value: data.instagram_username || "—" },
            { section: "contact", label: "Preferred Channel", value: data.preferred_communication || "—" },
            { section: "contact", label: "Detected Country", value: data.detected_country || "—" },
        ];

    const sectionGroups = isCustom
        ? [
            { key: "services", title: "Services", stepIndex: servicesEditIdxCustom },
            { key: "brand", title: "Brand", stepIndex: brandEditIdxCustom },
            { key: "vision", title: "Vision", stepIndex: visionEditIdxCustom },
            { key: "budget", title: "Investment", stepIndex: budgetEditIdxCustom },
            { key: "contact", title: "Contact", stepIndex: contactEditIdxCustom },
        ]
        : [
            { key: "package", title: "Package", stepIndex: packageEditIdx },
            { key: "brand", title: "Brand", stepIndex: brandEditIdxPackage },
            { key: "vision", title: "Vision", stepIndex: visionEditIdxPackage },
            { key: "timeline", title: "Timeline", stepIndex: timelineEditIdxPackage },
            { key: "contact", title: "Contact", stepIndex: contactEditIdxPackage },
        ];

    // Eyebrow index differs between journeys (8 vs 7 total).
    const stepEyebrow = isCustom ? "Step 07 · Review" : "Step 08 · Review";
    const title = isCustom ? "Ready to send your Custom Project Inquiry." : "Ready to send.";
    const description = isCustom
        ? "Take a final look. You can jump back to any section — nothing is submitted until you hit Send. We'll reply with a Custom Engagement proposal within 24h."
        : "Take a final look. You can jump back to any section — nothing is submitted until you hit Send.";

    return (
        <section data-testid="step-review">
            <SectionHead
                eyebrow={stepEyebrow}
                title={title}
                description={description}
            />

            {/* Custom Project summary card at the top */}
            {isCustom && (
                <div
                    className="mb-8 rounded-3xl border p-6 sm:p-7"
                    style={{
                        borderColor: "color-mix(in srgb, var(--pg-accent) 35%, var(--pg-border))",
                        background: "linear-gradient(135deg, color-mix(in srgb, var(--pg-accent) 6%, transparent) 0%, var(--pg-surface) 100%)",
                    }}
                    data-testid="review-custom-summary"
                >
                    <span className="text-[11px] font-medium tracking-normal" style={{ color: "var(--pg-accent)" }}>
                        Custom Project Inquiry
                    </span>
                    <h3 className="font-display mt-2 text-2xl" style={{ color: "var(--pg-fg)" }}>
                        {(data.services_needed || []).length} service{(data.services_needed || []).length === 1 ? "" : "s"} selected
                    </h3>
                    <p className="mt-3 text-sm leading-relaxed" style={{ color: "var(--pg-fg-muted)" }}>
                        We'll review your brief and reply with a tailored Custom Engagement proposal —
                        including scope, deliverables, and pricing — within 24h on business days.
                    </p>
                    <div className="mt-4 flex flex-wrap gap-1.5">
                        {(data.services_needed || []).map((sid) => (
                            <span
                                key={sid}
                                className="inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-medium tracking-normal"
                                style={{ borderColor: "var(--pg-border)", color: "var(--pg-fg)", background: "var(--pg-bg)" }}
                            >
                                {resolveServiceLabel(sid)}
                            </span>
                        ))}
                    </div>
                    <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div>
                            <span className="text-[11px] font-medium tracking-normal" style={{ color: "var(--pg-fg-muted)" }}>Budget</span>
                            <div className="mt-1 font-display text-lg" style={{ color: "var(--pg-fg)" }}>{finalBudget}</div>
                        </div>
                        <div>
                            <span className="text-[11px] font-medium tracking-normal" style={{ color: "var(--pg-fg-muted)" }}>Timeline</span>
                            <div className="mt-1 font-display text-lg" style={{ color: "var(--pg-fg)" }}>{data.expected_timeline || "—"}</div>
                        </div>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                {sectionGroups.map((group) => {
                    const sectionRows = rows.filter((r) => r.section === group.key);
                    return (
                        <div
                            key={group.key}
                            className="rounded-2xl border p-5"
                            style={{ borderColor: "var(--pg-border)", background: "var(--pg-surface)" }}
                            data-testid={`review-section-${group.key}`}
                        >
                            <div className="mb-4 flex items-center justify-between gap-3">
                                <span className="font-display text-[15px] font-bold tracking-normal" style={{ color: "var(--pg-fg)" }}>
                                    {group.title}
                                </span>
                                <button
                                    type="button"
                                    onClick={() => onEdit(group.stepIndex)}
                                    data-testid={`review-edit-${group.key}`}
                                    className="inline-flex shrink-0 items-center gap-1 rounded-full border px-3 py-1 text-[11px] font-medium tracking-normal transition-colors hover:brightness-105"
                                    style={{
                                        color: "var(--pg-accent)",
                                        borderColor: "color-mix(in srgb, var(--pg-accent) 40%, var(--pg-border))",
                                        background: "color-mix(in srgb, var(--pg-accent) 8%, transparent)",
                                    }}
                                >
                                    Edit
                                </button>
                            </div>
                            <dl className="space-y-2.5">
                                {sectionRows.map((r) => (
                                    <div key={r.label} className="grid grid-cols-[120px_1fr] gap-3 text-[13px]">
                                        <dt className="text-[12px] font-medium tracking-normal" style={{ color: "color-mix(in srgb, var(--pg-fg) 62%, var(--pg-fg-muted))" }}>
                                            {r.label}
                                        </dt>
                                        <dd className="whitespace-pre-wrap break-words" style={{ color: "var(--pg-fg)" }}>
                                            {r.value}
                                        </dd>
                                    </div>
                                ))}
                            </dl>
                        </div>
                    );
                })}
            </div>

            {/* Invoice + coupon (Package journey with a real fixed price) */}
            {!isCustom && !data.is_custom_scope && selectedPkg && (
                <div className="mt-8" data-testid="review-invoice-wrap">
                    <ReviewInvoice
                        selectedPackage={selectedPkg}
                        selectedAddons={selectedAddons}
                        basePrice={pricing.basePrice}
                        estimatedTotal={pricing.estimatedTotal}
                        couponCode={data.coupon_code}
                        couponPercent={data.coupon_percent}
                        onApplyCoupon={onApplyCoupon}
                        onRemoveCoupon={onRemoveCoupon}
                    />
                </div>
            )}

            {/* Custom-scope (bespoke inside package flow) — no fixed price to discount */}
            {!isCustom && data.is_custom_scope && (
                <div className="mt-8" data-testid="review-custom-scope">
                    <PricingSummary
                        selectedPackage={selectedPkg}
                        selectedAddons={selectedAddons}
                        basePrice={pricing.basePrice}
                        addOnsTotal={pricing.addOnsTotal}
                        estimatedTotal={pricing.estimatedTotal}
                        isCustomScope={data.is_custom_scope}
                        customBudgetDisplay={finalBudget}
                        accent={selectedPkg?.kind === "video" ? "cyan" : "gold"}
                        testIdPrefix="review-pricing-summary"
                    />
                </div>
            )}
            {errors && Object.keys(errors).length > 0 && (
                <div
                    className="mt-6 flex items-start gap-2 rounded-2xl border p-4 text-sm"
                    style={{ borderColor: "rgba(255,107,107,0.35)", background: "rgba(255,107,107,0.06)", color: "#FF6B6B" }}
                    data-testid="review-errors"
                >
                    <AlertCircle className="mt-0.5 h-4 w-4" />
                    <div>
                        <div className="text-[11px] font-medium tracking-normal">Fix these before sending</div>
                        <ul className="mt-2 space-y-1">
                            {Object.entries(errors).map(([k, v]) => (
                                <li key={k}>• {v}</li>
                            ))}
                        </ul>
                    </div>
                </div>
            )}

            {submitError && (
                <div
                    className="mt-6 flex items-start gap-2 rounded-2xl border p-4 text-sm"
                    style={{ borderColor: "rgba(255,107,107,0.35)", background: "rgba(255,107,107,0.06)", color: "#FF6B6B" }}
                    data-testid="review-submit-error"
                >
                    <AlertCircle className="mt-0.5 h-4 w-4" />
                    <div>{submitError}</div>
                </div>
            )}

            <motion.div
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 }}
                className="mt-10 flex flex-col items-start gap-4"
            >
                <button
                    type="button"
                    onClick={onSubmit}
                    disabled={submitting}
                    data-testid="review-submit-button"
                    className="pg-btn-flow"
                >
                    {submitting ? (
                        <>
                            <Loader2 className="h-5 w-5 animate-spin" strokeWidth={1.8} />
                            Sending…
                        </>
                    ) : (
                        <>
                            <CheckCircle2 className="h-5 w-5" strokeWidth={1.8} />
                            Send inquiry
                            <ArrowRight className="h-5 w-5" strokeWidth={1.8} />
                        </>
                    )}
                </button>
                <p className="text-[11px]" style={{ color: "var(--pg-fg-muted)" }}>
                    We'll email a private confirmation and Pranto will personally follow up within 24h on business days.
                </p>
                <p className="text-[12px]" style={{ color: isCustom || data.is_custom_scope ? "var(--pg-fg-muted)" : "var(--pg-accent)" }}>
                    {isCustom
                        ? "We'll reply with a Custom Engagement proposal within 24h."
                        : data.is_custom_scope
                            ? "You will get a custom quote within 24h."
                            : "Your total is shown in the invoice above — locked in at submit time."}
                </p>
            </motion.div>
        </section>
    );
};

export default StepReview;
